import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { v4 as uuidV4 } from 'uuid';
import { authService, logEvents, storageService } from '@/shared/services';
import { getConfig } from '@/shared/configs';

export enum eMetricType {
  COUNTER = 'COUNTER',
  VIDEO_ACTION = 'VIDEO_ACTION',
  VIDEO_METADATA = 'VIDEO_METADATA',
  ENUM = 'ENUM',
  STRING = 'STRING',
  DATETIME = 'DATETIME',
  BOOLEAN = 'BOOLEAN',
  NUMBER = 'NUMBER',
}

export interface StatTag {
  key: string,
  value: string
}

export interface StatEvent {
  key?: string;
  metricType: eMetricType,
  metricGroup: string,
  source?: string,
  value: any,
  tags: StatTag[],
  collectedAt?: number,
}

interface CurrentPage {
  path: string,
  startDate: Date,
}

// step between artificial timestamps taken on the same millisecond, required for correct server side processing
const TIMESTAMP_STEP = 0.001;

let lastUsedTimestamp = 0;

export class IgcStatisticsService {
  private events: StatEvent[] = [];

  private subject = new Subject<StatEvent[]>();

  private currentPage?: CurrentPage = undefined;

  private appName = '';

  private interval: any;

  constructor() {
    const eventChange = this.subject.pipe(debounceTime(1000));
    const eventsStorage = storageService.session.get('EVENTS');
    const pageStorage = storageService.session.get('CURRENT_PAGE');

    this.interval = setInterval(() => {
      const arr = this.events;

      this.checkEventsChange(arr);
    }, 10000);

    eventChange.subscribe((arr: any) => {
      this.checkEventsChange(arr);
    });

    if (eventsStorage) {
      const storedEvents = JSON.parse(eventsStorage);

      if (storedEvents && storedEvents.length > 0) {
        this.subject.next(this.events);
      }
    }

    if (pageStorage) {
      const curPage: any = JSON.parse(pageStorage);

      this.currentPage = {
        path: curPage.path,
        startDate: new Date(Date.parse(curPage.startDate)),
      };
    }
  }

  checkEventsChange(arr: any) {
    // TODO: DGC-36248 improve logged in check for Controlled Mode statistics - if we need to collect it.
    if (authService.getAuthToken() && arr && arr.length > 0) {
      this.sendChangeToServer(arr);
    }
  }

  getNextCollectedAt(): number {
    const currentTimestamp = new Date().getTime();

    if (currentTimestamp > lastUsedTimestamp) {
      lastUsedTimestamp = currentTimestamp;
    } else {
      lastUsedTimestamp += TIMESTAMP_STEP;
    }

    return lastUsedTimestamp;
  }

  initSource(appName: string) {
    this.appName = appName;
  }

  sendChangeToServer(events: StatEvent[]) {
    if (!import.meta.env.VUE_APP_DISABLE_STATISTICS) {
      logEvents(events);
    }
    this.events = [];
    storageService.session.set('EVENTS', JSON.stringify(this.events));
  }

  addEvent(evt: StatEvent) {
    const sessionId = storageService.session.get(getConfig().SESSION_ID_KEY);

    evt.tags.push({
      key: 'access-session-id',
      value: sessionId || '',
    });
    evt.collectedAt = this.getNextCollectedAt();
    evt.source = this.appName;
    evt.key = evt.key ? evt.key : uuidV4();

    this.events.push(evt);
    this.storeAndNext();
  }

  editEventValue(evt: StatEvent, value: any, tags?: any) {
    evt.value = value;
    evt.collectedAt = this.getNextCollectedAt();
    evt.tags = tags || evt.tags;

    this.storeAndNext();
  }

  storeAndNext() {
    this.subject.next(this.events);
    storageService.session.set('EVENTS', JSON.stringify(this.events));
  }

  addButtonClickEvent(eventName: string) {
    const evt: StatEvent = {
      metricType: eMetricType.COUNTER,
      metricGroup: 'button-click',
      value: 1,
      tags: [{
        key: 'event-name',
        value: eventName,
      }],
    };

    this.addEvent(evt);
  }

  addWSRecoveryEvent(key?: string) {
    const evt: StatEvent = {
      key,
      metricType: eMetricType.COUNTER,
      metricGroup: 'ws-connection-established',
      value: 1,
      tags: [],
    };

    this.addEvent(evt);
  }

  addVideoEvent(source: string, eventName: string) {
    const evt: StatEvent = {
      metricType: eMetricType.VIDEO_ACTION,
      metricGroup: eventName,
      value: 1,
      tags: [{
        key: 'video-source',
        value: source,
      }],
    };

    this.addEvent(evt);

    const counterEvt: StatEvent = {
      metricType: eMetricType.COUNTER,
      metricGroup: eventName,
      value: 1,
      tags: [{
        key: 'video-source',
        value: source,
      }],
    };

    this.addEvent(counterEvt);
  }

  addVideoTimeEvent(source: string, played: any, totalTime: number, key: string) {
    const tags = [];
    let playingTime = 0;

    for (let i = 0; i < played.length; i += 1) {
      playingTime += (played.end(i) - played.start(i));

      tags.push({
        key: 'start',
        value: played.start(i),
      });

      tags.push({
        key: 'start',
        value: played.end(i),
      });
    }

    tags.push({
      key: 'video-source',
      value: source,
    });

    tags.push({
      key: 'total-time',
      value: totalTime,
    });

    const value = ((100 * playingTime) / totalTime);
    const prevEvt = this.events.find(x => x.key === key);

    if (prevEvt) {
      this.editEventValue(prevEvt, value, tags);
    } else {
      const evt: StatEvent = {
        key,
        metricType: eMetricType.VIDEO_METADATA,
        metricGroup: 'video-progress-percentage',
        value,
        tags,
      };

      this.addEvent(evt);
    }
  }

  addQuizEvent(question: string, answer: string, isCorrect: boolean, key?: string) {
    const evt: StatEvent = {
      key,
      metricType: eMetricType.BOOLEAN,
      metricGroup: 'quiz-answer',
      value: isCorrect ? 'CORRECT' : 'WRONG',
      tags: [{
        key: 'question',
        value: question,
      }, {
        key: 'answer',
        value: answer,
      }],
    };

    this.addEvent(evt);
  }

  addDocumentViewEvent(src: string, name: string, key?: string) {
    const evt: StatEvent = {
      key,
      metricType: eMetricType.COUNTER,
      metricGroup: name,
      value: 1,
      tags: [{
        key: 'pdf-src',
        value: src,
      }, {
        key: 'location',
        value: window.location.pathname,
      }],
    };

    this.addEvent(evt);
  }

  addPageStart(pageName: string) {
    this.currentPage = {
      path: pageName,
      startDate: new Date(),
    };
    storageService.session.set('CURRENT_PAGE', JSON.stringify(this.currentPage));
  }

  addPageTimeEvent(pageName: string, key?: string) {
    if (this.currentPage && this.currentPage.path !== pageName) {
      const endDate = new Date();
      const seconds = (endDate.getTime() - this.currentPage.startDate.getTime()) / 1000;
      const evt: StatEvent = {
        key,
        metricType: eMetricType.NUMBER,
        metricGroup: 'access.page.time',
        value: seconds,
        tags: [{
          key: 'screen-name',
          value: this.currentPage.path,
        }],
      };

      this.addEvent(evt);
    }
    if (pageName !== '') {
      this.addPageStart(pageName);
    }
  }
}
